import { Banner, BoundSelectField, Button, Css, FormLines, IconButton, useComputed, useModal } from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { Maybe } from "src/generated/graphqlTypes";
import { BidPackageDetailPageDocument } from "src/routes/bids/BidPackageDetailPage.generated";
import {
  AwardBidModal_BidPackageFragment,
  useAwardBidModalQuery,
  useSaveBidPackageAwardsMutation,
} from "src/routes/bids/components/AwardBidModal.generated";
import { queryResult } from "src/utils";

type AwardBidModalProps = { bidPackageId: string };

export function AwardBidModal({ bidPackageId }: AwardBidModalProps) {
  const query = useAwardBidModalQuery({ variables: { bidPackageId }, fetchPolicy: "network-only" });
  return queryResult(query, ({ bidPackage: awardBidPackage }) => <AwardBidModalView bidPackage={awardBidPackage} />);
}

type AwardBidModalViewProps = { bidPackage: AwardBidModal_BidPackageFragment };
export function AwardBidModalView({ bidPackage }: AwardBidModalViewProps) {
  const { closeModal } = useModal();
  const [saveBidPackageAwards] = useSaveBidPackageAwardsMutation({ refetchQueries: [BidPackageDetailPageDocument] });

  const formState = useFormState({
    config: formConfig,
    init: { input: bidPackage, map: (bidPackage) => mapOrCreateBidPackageAwards(bidPackage) },
  });

  const newOverlappingCostCodes = useComputed(() => {
    const existingCostCodeIds = bidPackage.awards.map((award) => award.costCode.id);
    // get all the cost codes
    const allCCIds = allCostCodeIds(bidPackage);

    // If we are creating new awards we should check if there are any overlapping bid packages / cost codes
    if (!existingCostCodeIds.hasSameElements(allCCIds) && formState.primaryRevisionId.value) {
      const newCostCodes = allCCIds.filter((ccId) => !existingCostCodeIds.includes(ccId));
      const allOverlappingCostCodes = bidPackage.overlappingBidPackages
        .flatMap((bp) => bp.latestAwards)
        .map((a) => a.costCode)
        .uniqueByKey("id");

      return allOverlappingCostCodes.filter((costCode) => newCostCodes.includes(costCode.id));
    }
    return [];
  }, [bidPackage, formState]);

  async function saveAndExit() {
    await saveBidPackageAwards({
      variables: {
        input: {
          // Map the selected primary and secondary revisions out to every cost code in the bid package
          bidPackageAwards: allCostCodeIds(bidPackage).map((ccId) => ({
            costCodeId: ccId,
            primaryRevisionId: formState.primaryRevisionId.value,
            secondaryRevisionId: formState.secondaryRevisionId.value,
            bidPackageVersionId: bidPackage.latestVersion.id,
          })),
        },
      },
    });
    closeModal();
  }

  // Pair each active bidder with its BCR (match by bidContract.id). Works for both trade-partner
  // and internal-estimate bidders, so plan-based v2 internal estimates show up here too.
  const bidderOptions = bidPackage.activeBidders.map((bidder) => ({
    ...bidder,
    bidContractRevision: bidPackage.latestVersion.latestBidContractRevisions.find(
      (bcr) => bcr.bidContract.id === bidder.bidContract?.id,
    ),
  }));

  return (
    <div css={Css.df.fdc.p2.bgGray100.h100.$}>
      <div css={Css.asfe.pr1.$}>
        <IconButton icon="x" onClick={closeModal} />
      </div>
      {newOverlappingCostCodes.nonEmpty && (
        <div css={Css.relative.z1.$}>
          <Banner
            message={`A bid package already exists for ${newOverlappingCostCodes.map((cc) => cc.displayName).join(", ")}. Continuing will overwrite the existing bid package.`}
            type="warning"
          />
        </div>
      )}
      <div css={Css.xl2.asc.mt1.$} data-testid="award_title">
        Award Bid
      </div>
      <div css={Css.md.asc.pt2.$}>
        Here you can assign primary and secondary trades for specific scopes of work. After saving, you'll be able to
      </div>
      <div css={Css.md.asc.$}>award and release work to the selected trades when needed.</div>
      <div css={Css.p4.$}>
        <div css={Css.bshBasic.br8.df.oh.bgWhite.p6.w50.mxa.$}>
          <FormLines>
            <BoundSelectField
              field={formState.primaryRevisionId}
              label="Primary"
              options={bidderOptions}
              getOptionLabel={(b) => b.name}
              getOptionValue={(b) => b.bidContractRevision?.id ?? ""}
            />
            <BoundSelectField
              field={formState.secondaryRevisionId}
              label="Secondary"
              options={bidderOptions}
              getOptionLabel={(b) => b.name}
              getOptionValue={(b) => b.bidContractRevision?.id ?? ""}
            />
          </FormLines>
        </div>
      </div>
      <div css={Css.fixed.bottom0.w100.px6.py3.bshHover.df.$}>
        <div css={Css.mla.$}>
          <Observer>
            {() => <Button label="Save & Return" onClick={saveAndExit} size="lg" disabled={!formState.dirty} />}
          </Observer>
        </div>
      </div>
    </div>
  );
}

type FormValues = { primaryRevisionId: Maybe<string>; secondaryRevisionId: Maybe<string> };

const formConfig: ObjectConfig<FormValues> = {
  primaryRevisionId: { type: "value" },
  secondaryRevisionId: { type: "value" },
};

/*
 * The UI treats the entire bid package as an atomic award.
 * But the backend is still tracking awards by cost code, so we infer the awardees by looking at the per-cost code awards.
 */
function mapOrCreateBidPackageAwards(bidPackage: AwardBidModal_BidPackageFragment) {
  const callCostCodeIds = allCostCodeIds(bidPackage);
  const existingCostCodeIds = bidPackage.awards.map((award) => award.costCode.id);
  const allCostCodesAwarded = existingCostCodeIds.hasSameElements(callCostCodeIds);
  const firstAward = bidPackage.awards.first;
  return {
    primaryRevisionId: allCostCodesAwarded ? firstAward?.primaryRevision.id : undefined,
    secondaryRevisionId: allCostCodesAwarded ? firstAward?.secondaryRevision?.id : undefined,
  };
}

function allCostCodeIds(bidPackage: AwardBidModal_BidPackageFragment) {
  if (bidPackage.isPlanBasedV2) {
    return bidPackage.costCodes.map((cc) => cc.id);
  }
  return bidPackage.isUnitBased
    ? bidPackage.lineItems
        .map((bpli) => bpli.bidItem.items.first?.costCode)
        .compact()
        .uniqueByKey("id")
        .map((cc) => cc.id)
    : bidPackage.scopeLines
        .map((sl) => sl.takeoffLineItemVersion.item.costCode)
        .uniqueByKey("id")
        .map((cc) => cc.id);
}
